import React, { useState, useEffect } from 'react';
import { useAxios } from '../../core/_axios';
import { useAuth } from '../../AuthContext';
import { useOutletContext } from 'react-router-dom';
import { CampaignStats } from '../../core/_models_email';
import { getEmailCampaignStats } from '../../core/_requests_email';
import LoadingSpinner from '../../components/LoadingSpinner';

interface TrainingProgress {
  completed_count: number;
  required_count: number;
  is_complete: boolean;
  remaining: number;
}

interface OutletContext {
  trainingProgress: TrainingProgress | null;
}

const EmailAnalytics: React.FC = () => {
  const axios = useAxios();
  const { currentUserAccountUniqueId, viewingAllAccounts } = useAuth();
  const { trainingProgress } = useOutletContext<OutletContext>();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [dateRange, setDateRange] = useState('last_30_days');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, [dateRange]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const campaignStats = await getEmailCampaignStats(axios, 
        viewingAllAccounts ? null : currentUserAccountUniqueId, 
        dateRange,
        viewingAllAccounts);
      setStats(campaignStats);
    } catch (err) {
      console.error('Failed to load stats:', err);
      setError('Failed to load analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if still in training mode
  if (trainingProgress && !trainingProgress.is_complete) {
    return (
      <div className="card">
        <div className="card-body text-center py-12">
          <i className="ki-filled ki-chart-line text-5xl text-gray-400 mb-4"></i>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Analytics Not Yet Available
          </h3>
          <p className="text-gray-600 mb-4">
            Analytics will be available after you complete the training period.
          </p>
          <div className="text-sm text-gray-500">
            <strong>{trainingProgress.remaining}</strong> more email{trainingProgress.remaining !== 1 ? 's' : ''} need{trainingProgress.remaining === 1 ? 's' : ''} to be approved and sent before analytics become available.
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-center gap-2">
              <span className="text-sm text-gray-600">Training Progress:</span>
              <div className="progress progress-sm w-32">
                <div 
                  className="progress-bar bg-primary" 
                  style={{ width: `${(trainingProgress.completed_count / trainingProgress.required_count) * 100}%` }}
                ></div>
              </div>
              <span className="text-sm font-medium text-gray-700">
                {trainingProgress.completed_count}/{trainingProgress.required_count}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!stats) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        Failed to load analytics
      </div>
    );
  }

  const successRate = stats.total_campaigns > 0 
    ? Math.round((stats.sent / stats.total_campaigns) * 100) 
    : 0;

  const highQualityRate = stats.total_campaigns > 0
    ? Math.round((stats.high_quality_leads / stats.total_campaigns) * 100)
    : 0;

  const engagement = stats.engagement;

  return (
    <div className="grid gap-5 lg:gap-7.5">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold">Analytics</h3>
        <select
          className="form-select form-select-sm form-select-solid"
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
        >
          <option value="last_7_days">Last 7 days</option>
          <option value="last_30_days">Last 30 days</option>
          <option value="last_90_days">Last 90 days</option>
          <option value="all_time">All time</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Top 3 Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Engagement Rate */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-gray-900">
                  {engagement ? `${engagement.click_through_rate}%` : '0%'}
                </div>
                <div className="text-sm text-gray-600 mt-1">Engagement Rate</div>
                {engagement && engagement.total_sent > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    {engagement.total_clicked} of {engagement.total_sent} clicked
                  </div>
                )}
              </div>
              <div className="flex items-center justify-center size-12 rounded-full bg-primary-light">
                <i className="ki-filled ki-click text-primary text-xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Messages Sent */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-gray-900">{stats.sent}</div>
                <div className="text-sm text-gray-600 mt-1">Messages Sent</div>
                {stats.pending_review > 0 && (
                  <div className="text-xs text-warning mt-1">
                    {stats.pending_review} pending review
                  </div>
                )}
              </div>
              <div className="flex items-center justify-center size-12 rounded-full bg-success-light">
                <i className="ki-filled ki-sms text-success text-xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Average Lead Score */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-gray-900">
                  {Math.round(stats.average_lead_score)}
                </div>
                <div className="text-sm text-gray-600 mt-1">Avg Lead Score</div>
                <div className="text-xs text-gray-500 mt-1">
                  {stats.high_quality_leads} high quality (70+)
                </div>
              </div>
              <div className="flex items-center justify-center size-12 rounded-full bg-info-light">
                <i className="ki-filled ki-star text-info text-xl"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Engagement Details - Simplified Two Column Layout */}
      {engagement && engagement.total_sent > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Most Engaged Services */}
          <div className="card">
            <div className="card-header py-3">
              <h3 className="card-title text-base">Most Engaged Services</h3>
            </div>
            <div className="card-body">
              {engagement.top_engaged_services.length > 0 ? (
                <div className="space-y-3">
                  {engagement.top_engaged_services.slice(0, 5).map((service, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <a 
                          href={`/services/${service.service_identifier}`}
                          className="text-sm font-medium text-primary hover:text-primary-dark"
                        >
                          {service.deceased_name || 'Unknown'}
                        </a>
                        {viewingAllAccounts && service.account_name && (
                          <span className="text-xs text-gray-500">{service.account_name}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">{service.click_count}</span>
                        <i className="ki-filled ki-click text-xs text-gray-400"></i>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-4 text-sm">
                  No engagement data yet
                </div>
              )}
            </div>
          </div>

          {/* Highly Engaged Leads */}
          <div className="card">
            <div className="card-header py-3">
              <h3 className="card-title text-base">Highly Engaged Leads</h3>
            </div>
            <div className="card-body">
              {engagement.recent_engaged_users.filter(u => u.click_count > 1 || u.lead_score >= 70).length > 0 ? (
                <div className="space-y-3">
                  {engagement.recent_engaged_users
                    .filter(user => user.click_count > 1 || user.lead_score >= 70)
                    .slice(0, 5)
                    .map((user, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {user.signer_name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {user.signer_email}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          {user.click_count > 1 && (
                            <span className="badge badge-sm badge-light-success">
                              {user.click_count} clicks
                            </span>
                          )}
                          {user.lead_score >= 80 && (
                            <span className="badge badge-sm badge-light-warning">
                              Hot Lead
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-4 text-sm">
                  No highly engaged leads yet
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailAnalytics;